import React from 'react'
import {
  SanityContactForm,
  SanityFormTemplate,
  SanityInputCheckbox,
  SanityInputDropdown,
  SanityInputEmail,
  SanityInputNumber,
  SanityInputPassword,
  SanityInputPhoneNumber,
  SanityInputRadioButtons,
  SanityInputText,
  SanityInputTextArea,
} from '../../types/generated/graphql-types'
import { ContactFormProps } from '../components/blocks/ContactForm/ContactForm'
import { RichTextArea } from '../components/PortableText/RichTextArea'
import { InlineBlockSerializer } from '../components/PortableText/serializers/blocks/InlineBlockSerializer'
import {
  CheckboxInputProps,
  DropdownInputProps,
  FormInputProps,
  PhoneNumberInputProps,
  RadioButtonsInputProps,
  TextInputProps,
  Validator,
} from '../components/ReactHookForm/types'
import { portableTextToPlainText } from '../utilities/blockContent'
import { notEmpty } from '../utilities/filters'

type SanityInputs = NonNullable<SanityFormTemplate['inputs']>
type SanityValidations = NonNullable<SanityInputText['validation']>

type ValidatedSanityValidation = {
  _type: string
  message: string
  value?: string | number | null
}

const notEmptyValidation = (
  validation: SanityValidations[number]
): validation is ValidatedSanityValidation =>
  !!validation && !!validation._type && !!validation.message

const mapValidatedSanityValidationToValidator = (
  validation: ValidatedSanityValidation,
  inputLabel?: string
): Validator => {
  const replacementLabel = inputLabel ?? ''
  const replacementValue =
    typeof validation.value === 'number' ? `${validation.value}` : validation.value ?? ''

  return {
    message: validation.message
      .replace('[label]', replacementLabel.toLowerCase())
      .replace('[value]', replacementValue),
    type: validation._type,
    value: validation.value ?? undefined,
  }
}

const getCommonInputProps = <T extends Partial<FormInputProps>>(
  input: NonNullable<SanityInputs[number]>,
  labelOverride?: string
): T => {
  const label = labelOverride ?? input.label ?? undefined

  return {
    label,
    slug: input.slug?.current ?? '',
    validation: input.validation
      ?.filter(notEmptyValidation)
      .map(validationItem =>
        mapValidatedSanityValidationToValidator(validationItem, label)
      ),
  } as T
}

const sanityInputTextToTextInputProps = (
  input: SanityInputText
): Partial<TextInputProps> => ({
  ...getCommonInputProps(input),
  defaultValue: input.defaultValue ?? undefined,
  placeholder: input.placeholder ?? undefined,
  hidden: input.hidden ?? undefined,
  type: 'inputText',
})

const sanityInputEmailToTextInputProps = (
  input: SanityInputEmail
): Partial<TextInputProps> => ({
  ...getCommonInputProps(input),
  placeholder: input.placeholder ?? undefined,
  type: 'inputEmail',
})

const sanityInputNumberToTextInputProps = (
  input: SanityInputNumber
): Partial<TextInputProps> => ({
  ...getCommonInputProps(input),
  placeholder: input.placeholder ?? undefined,
  type: 'inputNumber',
})

const sanityInputTextAreaToTextInputProps = (
  input: SanityInputTextArea
): Partial<TextInputProps> => ({
  ...getCommonInputProps(input),
  placeholder: input.placeholder ?? undefined,
  type: 'inputTextArea',
})

const sanityInputDropdownToDropdownInputProps = (
  input: SanityInputDropdown
): Partial<DropdownInputProps> => ({
  ...getCommonInputProps(input),
  defaultValue: input.defaultValue ?? undefined,
  options: input.options?.filter(notEmpty).map(option => ({
    label: option.value ?? '',
    value: option.value ?? '',
  })),
  placeholder: input.placeholder ?? undefined,
  type: 'inputDropdown',
})

const sanityInputPhoneNumberToPhoneNumberInputProps = (
  input: SanityInputPhoneNumber
): Partial<PhoneNumberInputProps> => ({
  ...getCommonInputProps(input),
  placeholder: input.placeholder ?? undefined,
  type: 'inputPhoneNumber',
})

const sanityInputCheckboxToCheckboxInputProps = (
  input: SanityInputCheckbox & { defaultValueBool?: boolean }
): Partial<CheckboxInputProps> => {
  let labelOverride: string | undefined

  if (input.useRichText) {
    labelOverride = !!input._rawLabelFormatted?.length
      ? portableTextToPlainText(input._rawLabelFormatted)
      : undefined
  }

  return {
    ...getCommonInputProps(input, labelOverride),
    defaultValue: input.defaultValueBool ?? undefined,
    labelFormatted:
      input.useRichText && !!input._rawLabelFormatted?.length ? (
        <RichTextArea
          components={{ block: InlineBlockSerializer }}
          value={input._rawLabelFormatted}
        />
      ) : undefined,
    type: 'inputCheckbox',
  }
}

const sanityInputRadioButtonsToRadioButtonsInputProps = (
  input: SanityInputRadioButtons
): Partial<RadioButtonsInputProps> => ({
  ...getCommonInputProps(input),
  defaultValue: input.defaultValue ?? undefined,
  options: input.options?.filter(notEmpty).map(option => ({
    label: option.value ?? '',
    value: option.value ?? '',
  })),
  type: 'inputRadioButtons',
})

const sanityInputsToFormInputProps = (inputs: SanityInputs): Partial<FormInputProps>[] =>
  inputs.filter(notEmpty).map<Partial<FormInputProps>>(input => {
    switch (input._type) {
      default:
      case 'inputText':
        return sanityInputTextToTextInputProps(input as SanityInputText)
      case 'inputEmail':
        return sanityInputEmailToTextInputProps(input as SanityInputEmail)
      case 'inputNumber':
        return sanityInputNumberToTextInputProps(input as SanityInputNumber)
      case 'inputPassword':
        return getCommonInputProps(input as SanityInputPassword)
      case 'inputTextArea':
        return sanityInputTextAreaToTextInputProps(input as SanityInputTextArea)
      case 'inputDropdown':
        return sanityInputDropdownToDropdownInputProps(input as SanityInputDropdown)
      case 'inputCheckbox':
        return sanityInputCheckboxToCheckboxInputProps(input as SanityInputCheckbox)
      case 'inputRadioButtons':
        return sanityInputRadioButtonsToRadioButtonsInputProps(
          input as SanityInputRadioButtons
        )
      case 'inputPhoneNumber':
        return sanityInputPhoneNumberToPhoneNumberInputProps(
          input as SanityInputPhoneNumber
        )
    }
  })

export const sanityContactFormToContactFormProps = ({
  errorMessageBody,
  errorMessageTitle,
  form,
  preamble,
  successMessageBody,
  successMessageTitle,
  successRedirectPage,
  successFileDownload,
  title,
}: SanityContactForm): ContactFormProps => ({
  errorMessageBody: errorMessageBody ?? undefined,
  errorMessageTitle: errorMessageTitle ?? undefined,
  formProps: form?.slug?.current
    ? {
        inputs: form.inputs ? sanityInputsToFormInputProps(form.inputs) : undefined,
        slug: form.slug.current,
        submitText: form.submitText ?? '',
      }
    : undefined,
  preamble: preamble ?? undefined,
  successMessageBody: successMessageBody ?? undefined,
  successMessageTitle: successMessageTitle ?? undefined,
  successRedirectUrl: successRedirectPage?.page?.path ?? undefined,
  successRedirectFileUrl: successFileDownload?.asset?.url ?? undefined,
  title: title ?? undefined,
})
